
// src/composables/useBarOptions.ts
import { computed } from 'vue'
import type { TooltipItem } from 'chart.js'

export function useBarOptions(title: string, unit: string, fractionDigits = 0) {
  function getThemeMeta() {
    const isDark = document.documentElement.classList.contains('v-theme--dark')
    const tick = isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)'
    const grid = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
    const titleColor = tick
    const dlBg = 'rgba(33,150,243,0.85)'
    return { tick, grid, titleColor, dlBg }
  }
  const theme = getThemeMeta()

  return computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 200,
    animation: { duration: 300, easing: 'easeOutQuart' },
    layout: { padding: { top: 8, right: 6, bottom: 6, left: 6 } },
    plugins: {
      title: {
        display: true,
        text: title,
        color: theme.titleColor,
        font: { weight: '600', size: 14 },
        padding: { top: 4, bottom: 8 },
      },
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label(context: TooltipItem<'bar'>) {
            const v = Number(context.raw)
            const formatted = isFinite(v)
              ? v.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
              : ''
            return formatted ? `${formatted} ${unit}` : ''
          },
        },
      },
      datalabels: {
        formatter(value: unknown) {
          const n = typeof value === 'number' ? value : Number(value)
          return isFinite(n)
            ? n.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
            : ''
        },
        color: 'white',
        backgroundColor: theme.dlBg,
        borderRadius: 6,
        font: { weight: 'bold' },
        offset: 2,
        padding: 4,
        anchor: 'center',
        align: 'center',
      },
    },
    scales: {
      x: { ticks: { display: true, color: theme.tick }, grid: { display: false } },
      y: {
        beginAtZero: true,
        title: { display: true, text: unit, color: theme.tick },
        ticks: {
          display: true,
          color: theme.tick,
          callback(v: number) {
            return Number(v).toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })
          },
        },
        grid: { color: theme.grid },
      },
    },
  }))
}
